import {
  Action,
  CUSTOM_SEPARATOR_CHANGED,
  EXAMPLE_DATA_PICKED,
  INPUT_CHANGED,
  MERGE_REQUESTED,
  RESULT_CHANGED,
  RESULTS_VISIBILITY_CHANGED,
  SCROLL_NOW_CHANGED,
  SEPARATOR_CHANGED,
  WRAPPER_CHANGED
} from '../actions'
import texts from '../texts.json'
import {
  IStoreState,
  merge,
  SPACE,
  NOTHING,
  SEPARATORS,
  WRAPPERS,
  CUSTOM,
  IExtraOption
} from '../utils'

const DOMAINING = 'domaining'
const LINKBUILDING = 'linkbuilding'
const ADWORDS = 'adwords'
type ExampleName = typeof DOMAINING | typeof LINKBUILDING | typeof ADWORDS

export const DEFAULT_STATE = Object.freeze<IStoreState>({
  first: '',
  second: '',
  third: '',
  combinations: 0,
  prevCombinations: 0,
  merged: false,
  result: '',
  separator: SPACE,
  wrapper: NOTHING,
  scrollNow: false,
  resultsAreVisible: false,
  customSeparator: ''
})

const getCombinations = (state: IStoreState) => {
  const { first, second, third } = state

  const c1 = first.split('\n').length
  const c2 = second.split('\n').length
  const c3 = third.split('\n').length

  return c1 * c2 * c3
}

const inputChanged = (
  state: IStoreState,
  name: string,
  value: string
): IStoreState => {
  const newState = {
    ...state,
    [name]: value
  }

  return {
    ...newState,
    merged: false,
    prevCombinations: state.combinations,
    combinations: getCombinations(newState)
  }
}

const separatorChanged = (state: IStoreState, kind: string): IStoreState => {
  const found = SEPARATORS.find(sep => sep.kind === kind)
  if (!found) {
    return state
  }

  let separator: IExtraOption
  // for custom separator use value from the store
  if (found.kind === CUSTOM.kind) {
    separator = { ...found, value: state.customSeparator }
  } else {
    separator = found
  }

  return {
    ...state,
    separator
  }
}

const customSeparatorChanged = (
  state: IStoreState,
  value: string
): IStoreState => {
  return {
    ...state,
    separator: { ...CUSTOM, value },
    customSeparator: value
  }
}

const wrapperChanged = (state: IStoreState, kind: string): IStoreState => {
  const wrapper = WRAPPERS.find(wrap => wrap.kind === kind)
  if (!wrapper) {
    return state
  }

  return {
    ...state,
    wrapper
  }
}

const resultChanged = (state: IStoreState, result: string): IStoreState => {
  return {
    ...state,
    result
  }
}

const resultsVisibilityChanged = (
  state: IStoreState,
  resultsAreVisible: boolean
) => {
  return {
    ...state,
    resultsAreVisible
  }
}

const mergeRequested = (state: IStoreState): IStoreState => {
  return {
    ...state,
    result: merge(state),
    scrollNow: !state.resultsAreVisible,
    merged: true
  }
}

const scrollNowChanged = (state: IStoreState, scrollNow: boolean) => {
  return {
    ...state,
    scrollNow
  }
}

const reducers = (state: IStoreState = DEFAULT_STATE, action: Action) => {
  switch (action.type) {
    case INPUT_CHANGED:
      return inputChanged(state, action.name, action.value)
    case SEPARATOR_CHANGED:
      return separatorChanged(state, action.kind)
    case WRAPPER_CHANGED:
      return wrapperChanged(state, action.kind)
    case CUSTOM_SEPARATOR_CHANGED:
      return customSeparatorChanged(state, action.value)
    case RESULT_CHANGED:
      return resultChanged(state, action.result)
    case MERGE_REQUESTED:
      return mergeRequested(state)
    case RESULTS_VISIBILITY_CHANGED:
      return resultsVisibilityChanged(state, action.resultsAreVisible)
    case SCROLL_NOW_CHANGED:
      return scrollNowChanged(state, action.scrollNow)
    case EXAMPLE_DATA_PICKED:
      const exampleName = action.name as ExampleName
      const exampleTexts = texts.examples[exampleName]

      const nextState = {
        ...state,
        first: exampleTexts.first.join('\n'),
        second: exampleTexts.second.join('\n'),
        third: exampleTexts.third.join('\n')
      }

      if (exampleName === DOMAINING) {
        nextState.separator = NOTHING
      } else {
        nextState.separator = SPACE
      }
      nextState.wrapper = NOTHING

      nextState.combinations = getCombinations(nextState)
      nextState.prevCombinations = state.combinations
      nextState.merged = false
      return nextState
    default:
      return state
  }
}

export default reducers
